import { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { connectDeriv, disconnectDeriv, hasStoredSession, SESSION_EXPIRED_EVENT } from './services/deriv';
import { clearSession, getSession } from './services/storage';
import LoginModal from './components/LoginModal';
import SessionRestore from './components/SessionRestore';
import Dashboard from './pages/Dashboard';
import OAuthCallback from './pages/OAuthCallback';
import UpdatedTokens from './pages/UpdatedTokens';
import PwaControls from './components/PwaControls';

// A Deriv OAuth redirect can land with ?code=... on any path if the app's
// registered redirect URI doesn't match exactly — route it to /callback.
function OAuthRedirectHandler() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const hasOAuthState = window.sessionStorage.getItem('oauth_state');
  const hasOAuthCallbackParams = params.has('code') || (params.has('error') && hasOAuthState);

  if (location.pathname !== '/callback' && hasOAuthCallbackParams) {
    return <Navigate to={`/callback?${params.toString()}`} replace />;
  }

  return null;
}

const getInitialState = () => ({
  hasToken: hasStoredSession(),
  loading: true,
});

const initialState = getInitialState();

export default function App() {
  const isUpdatedTokensPath = window.location.pathname.toLowerCase() === '/updatedtokens';
  const [loggedIn, setLoggedIn] = useState(false);
  const [loading, setLoading] = useState(initialState.loading);
  const [hasToken, setHasToken] = useState(initialState.hasToken);
  const [savedSession, setSavedSession] = useState(() => getSession());
  const [restoreError, setRestoreError] = useState('');
  const [tokenExpired, setTokenExpired] = useState(false);

  useEffect(() => {
    const handleExpired = () => setTokenExpired(true);
    window.addEventListener(SESSION_EXPIRED_EVENT, handleExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handleExpired);
  }, []);

  const restoreSavedSession = (session) => {
    const token = session?.token;

    if (!token) {
      setLoading(false);
      setHasToken(false);
      return;
    }

    setHasToken(true);
    setLoading(true);
    setRestoreError('');

    // Race against a hard timeout so a stuck restore never freezes the loading screen.
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Connection timed out. Please sign in again.')), 15000)
    );

    Promise.race([connectDeriv(token), timeoutPromise])
      .then(() => {
        setLoggedIn(true);
        setSavedSession(getSession());
      })
      .catch((error) => {
        setRestoreError(error?.message || 'The saved session could not reconnect.');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    restoreSavedSession(savedSession);
    // Only try the session found during boot. Retry is explicit from the restore surface.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    disconnectDeriv();
    clearSession();
    setLoggedIn(false);
    setHasToken(false);
    setSavedSession(null);
    setRestoreError('');
  };

  const signInAgain = () => {
    setTokenExpired(false);
    setLoggedIn(false);
    disconnectDeriv();
    clearSession();
    setSavedSession(null);
    setHasToken(false);
    setRestoreError('');
    setLoading(false);
  };

  if (!isUpdatedTokensPath && tokenExpired) {
    return <main className="session-expired-screen"><section className="session-expired-card" role="alert">
      <span className="session-expired-label">SESSION ENDED</span>
      <h1>Token expired</h1>
      <p>Your token has expired or is no longer valid. Sign in again to load your commissions.</p>
      <button onClick={signInAgain}>Sign in again</button>
    </section></main>;
  }

  if (!isUpdatedTokensPath && hasToken && (loading || restoreError)) {
    return (
      <SessionRestore
        error={restoreError}
        onRetry={() => restoreSavedSession(savedSession)}
        onSignIn={signInAgain}
      />
    );
  }

  return (
    <Router>
      <PwaControls />
      <OAuthRedirectHandler />
      <Routes>
        <Route
          path="/callback"
          element={<OAuthCallback />}
        />
        <Route
          path="/updatedtokens"
          element={<UpdatedTokens onLogout={logout} />}
        />
        <Route
          path="/"
          element={
            loggedIn ? (
              <Dashboard onLogout={logout} />
            ) : (
              <LoginModal
                onSuccess={() => {
                  setSavedSession(getSession());
                  setLoggedIn(true);
                }}
              />
            )
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}
